"use client";

import { c as _c } from "react/compiler-runtime";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import VotingModalButton from "@/components/voting/VotingModalButton";
import DropCurationButton from "@/components/waves/drops/DropCurationButton";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropPartContentMarkdown from "@/components/waves/drops/WaveDropPartContentMarkdown";
import { LinkPreviewProvider } from "@/components/waves/LinkPreviewContext";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import React, { useCallback, useMemo, useState, useSyncExternalStore } from "react";
interface WaveLeaderboardGridItemViewportProps {
  readonly drop: ExtendedDrop;
  readonly isCompactMode: boolean;
  readonly isContentOnlyMode: boolean;
  readonly showDesktopContentOnlyActions: boolean;
  readonly canOpenDrop: boolean;
  readonly isCuratable: boolean;
  readonly isCurated: boolean;
  readonly canShowVotingAction: boolean;
  readonly onVoteButtonClick: () => void;
}
const getGridViewportClassName = ({
  hasMedia,
  isCompactMode
}: {
  readonly hasMedia: boolean;
  readonly isCompactMode: boolean;
}): string => isCompactMode || hasMedia ? "tw-relative tw-overflow-hidden tw-bg-iron-950" : "tw-relative tw-max-h-[20rem] tw-overflow-hidden tw-bg-iron-950";
const getGridContentSpacingClassName = (isCompactMode: boolean): string => isCompactMode ? "tw-space-y-3" : "tw-space-y-1";
const getGridMediaWrapperClassName = (isCompactMode: boolean): string => `tw-relative tw-flex tw-aspect-square tw-min-h-[14rem] tw-items-center tw-justify-center tw-overflow-hidden md:tw-min-h-[15rem] ${isCompactMode ? "tw-rounded-lg tw-bg-iron-900" : "tw-bg-iron-950"}`;
const getGridTextWrapperClassName = ({
  hasMedia,
  isCompactMode
}: {
  readonly hasMedia: boolean;
  readonly isCompactMode: boolean;
}): string => `tw-px-3 ${hasMedia ? "tw-pt-2" : "tw-pt-3"} ${isCompactMode ? "tw-pb-4" : "tw-pb-3"}`;
const getCompactTextViewportClassName = ({
  hasMedia,
  isCompactMode
}: {
  readonly hasMedia: boolean;
  readonly isCompactMode: boolean;
}): string | undefined => {
  if (!isCompactMode) {
    return undefined;
  }
  if (hasMedia) {
    return "tw-max-h-28 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300 [&_p]:tw-whitespace-normal";
  }
  return "tw-relative tw-max-h-56 tw-overflow-hidden [&_p]:tw-whitespace-normal";
};
function useOverflowGradient(t0) {
  const $ = _c(6);
  const {
    viewportEl,
    innerEl
  } = t0;
  let t1;
  if ($[0] !== innerEl || $[1] !== viewportEl) {
    t1 = () => {
      if (!viewportEl || !innerEl) {
        return false;
      }
      return innerEl.scrollHeight > viewportEl.clientHeight + 1;
    };
    $[0] = innerEl;
    $[1] = viewportEl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const getOverflowSnapshot = t1;
  let t2;
  if ($[3] !== innerEl || $[4] !== viewportEl) {
    t2 = onStoreChange => {
      if (!viewportEl || !innerEl) {
        return _temp;
      }
      if (typeof ResizeObserver === "undefined") {
        globalThis.addEventListener("resize", onStoreChange);
        return () => {
          globalThis.removeEventListener("resize", onStoreChange);
        };
      }
      const observer = new ResizeObserver(() => {
        onStoreChange();
      });
      observer.observe(viewportEl);
      observer.observe(innerEl);
      return () => {
        observer.disconnect();
      };
    };
    $[3] = innerEl;
    $[4] = viewportEl;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const subscribeToOverflow = t2;
  return useSyncExternalStore(subscribeToOverflow, getOverflowSnapshot, _temp2);
}
function _temp2() {
  return false;
}
function _temp() {}
export const WaveLeaderboardGridItemViewport: React.FC<WaveLeaderboardGridItemViewportProps> = t0 => {
  const $ = _c(49);
  const {
    drop,
    isCompactMode,
    isContentOnlyMode,
    showDesktopContentOnlyActions,
    canOpenDrop,
    isCuratable,
    isCurated,
    canShowVotingAction,
    onVoteButtonClick
  } = t0;
  const activePart = drop.parts[0];
  const primaryMedia = activePart?.media[0];
  const [viewportEl, setViewportEl] = useState(null);
  const [innerEl, setInnerEl] = useState(null);
  const [compactTextViewportEl, setCompactTextViewportEl] = useState(null);
  const [compactTextInnerEl, setCompactTextInnerEl] = useState(null);
  let t1;
  if ($[0] !== drop.metadata) {
    t1 = getDropPreviewImageUrl(drop.metadata);
    $[0] = drop.metadata;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewImageUrl = t1;
  const mediaUrl = primaryMedia?.url ?? null;
  const mediaMimeType = primaryMedia?.mime_type ?? "image/jpeg";
  let t2;
  if ($[2] !== mediaMimeType || $[3] !== previewImageUrl) {
    t2 = mediaMimeType.includes("image") ? null : previewImageUrl;
    $[2] = mediaMimeType;
    $[3] = previewImageUrl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const mediaPreviewImageUrl = t2;
  const hasMedia = mediaUrl !== null && mediaUrl.length > 0;
  const shouldRenderMarkdown = !(isContentOnlyMode && hasMedia);
  const shouldMeasureCompactTextOverflow = isCompactMode && !hasMedia;
  let t3;
  if ($[5] !== innerEl || $[6] !== viewportEl) {
    t3 = {
      viewportEl,
      innerEl
    };
    $[5] = innerEl;
    $[6] = viewportEl;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const showGradient = useOverflowGradient(t3);
  let t4;
  if ($[8] !== compactTextInnerEl || $[9] !== compactTextViewportEl) {
    t4 = {
      viewportEl: compactTextViewportEl,
      innerEl: compactTextInnerEl
    };
    $[8] = compactTextInnerEl;
    $[9] = compactTextViewportEl;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const showCompactTextGradient = useOverflowGradient(t4);
  let t5;
  if ($[11] !== hasMedia || $[12] !== isCompactMode) {
    t5 = getGridViewportClassName({
      hasMedia,
      isCompactMode
    });
    $[11] = hasMedia;
    $[12] = isCompactMode;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== isCompactMode) {
    t6 = getGridContentSpacingClassName(isCompactMode);
    $[14] = isCompactMode;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== hasMedia || $[17] !== isCompactMode || $[18] !== mediaMimeType || $[19] !== mediaPreviewImageUrl || $[20] !== mediaUrl) {
    t7 = hasMedia && <div className={getGridMediaWrapperClassName(isCompactMode)}><MediaDisplay media_mime_type={mediaMimeType} media_url={mediaUrl} disableMediaInteraction={true} imageScale={ImageScale.AUTOx450} previewImageUrl={mediaPreviewImageUrl} /></div>;
    $[16] = hasMedia;
    $[17] = isCompactMode;
    $[18] = mediaMimeType;
    $[19] = mediaPreviewImageUrl;
    $[20] = mediaUrl;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== activePart || $[23] !== drop || $[24] !== hasMedia || $[25] !== isCompactMode || $[26] !== shouldMeasureCompactTextOverflow || $[27] !== shouldRenderMarkdown || $[28] !== showCompactTextGradient) {
    t8 = activePart && shouldRenderMarkdown && <div className={getGridTextWrapperClassName({
      hasMedia,
      isCompactMode
    })}><div ref={shouldMeasureCompactTextOverflow ? setCompactTextViewportEl : undefined} className={getCompactTextViewportClassName({
        hasMedia,
        isCompactMode
      })}><div ref={shouldMeasureCompactTextOverflow ? setCompactTextInnerEl : undefined}><LinkPreviewProvider variant="home"><WaveDropPartContentMarkdown mentionedUsers={drop.mentioned_users} mentionedGroups={drop.mentioned_groups} mentionedWaves={drop.mentioned_waves} referencedNfts={drop.referenced_nfts} part={activePart} wave={drop.wave} drop={drop} onQuoteClick={_temp3} /></LinkPreviewProvider></div>{shouldMeasureCompactTextOverflow && showCompactTextGradient && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-14 tw-bg-gradient-to-t tw-from-iron-950 tw-via-iron-950/70 tw-to-transparent" />}</div></div>;
    $[22] = activePart;
    $[23] = drop;
    $[24] = hasMedia;
    $[25] = isCompactMode;
    $[26] = shouldMeasureCompactTextOverflow;
    $[27] = shouldRenderMarkdown;
    $[28] = showCompactTextGradient;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t9 = <div ref={setInnerEl} className={t6}>{t7}{t8}</div>;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== showGradient) {
    t10 = showGradient && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-14 tw-bg-gradient-to-t tw-from-iron-900 tw-via-iron-900/90 tw-to-transparent" />;
    $[34] = showGradient;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== canOpenDrop || $[37] !== canShowVotingAction || $[38] !== drop || $[39] !== isCuratable || $[40] !== isCurated || $[41] !== onVoteButtonClick || $[42] !== showDesktopContentOnlyActions) {
    t11 = showDesktopContentOnlyActions && <div data-testid={`wave-leaderboard-grid-item-content-only-actions-${drop.id}`} className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-0 tw-bg-gradient-to-t tw-from-black/90 tw-via-black/65 tw-to-transparent tw-p-2 tw-opacity-0 tw-transition-opacity tw-duration-200 group-focus-within:tw-opacity-100 desktop-hover:group-hover:tw-opacity-100"><div className="tw-pointer-events-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">{canOpenDrop && <div className="tw-flex tw-h-8 tw-items-center"><WaveDropActionsOpen drop={drop} /></div>}{isCuratable && <DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={isCuratable} isCurated={isCurated} className="tw-bg-iron-950/70" />}{canShowVotingAction && <VotingModalButton drop={drop} onClick={onVoteButtonClick} variant="subtle" />}</div></div>;
    $[36] = canOpenDrop;
    $[37] = canShowVotingAction;
    $[38] = drop;
    $[39] = isCuratable;
    $[40] = isCurated;
    $[41] = onVoteButtonClick;
    $[42] = showDesktopContentOnlyActions;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t5 || $[47] !== t9) {
    t12 = <div ref={setViewportEl} className={t5}>{t9}{t10}{t11}</div>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t5;
    $[47] = t9;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  return t12;
};
function _temp3() {}